import React, {useCallback} from 'react';
import {View, TouchableOpacity} from 'react-native';
import {useTheme} from 'styled-components';
import {Theme} from '../../theme/lightTheme';
import Icon from '../icon';
import TextGeneric from '../textGeneric';
import styled from 'styled-components/native';
import {isWeb} from '../../utils/responsive';
import {useDifferentialDiagnosisTable} from '../../hooks/useDifferentialDiagnosisTable';

const CheckboxContainer = styled(TouchableOpacity)`
  width: 24px;
  height: 24px;
  border-radius: 4px;
  border: 2px solid ${({theme}) => theme.text.light};
  align-items: center;
  justify-content: center;
  background-color: ${({checked, theme}: {checked: boolean; theme: Theme}) =>
    checked ? theme.primary : 'transparent'};
`;

const FabContainer = styled(View)`
  ${isWeb ? 'position: fixed;' : 'position: absolute;'}
  bottom: 24px;
  right: 24px;
  z-index: 1000;
`;

const FabButton = styled(TouchableOpacity)`
  flex-direction: row;
  align-items: center;
  background-color: ${({theme}) => theme.primary};
  border-radius: 28px;
  padding-vertical: 12px;
  padding-horizontal: 20px;
  shadow-color: ${({theme}) => theme.text.dark};
  shadow-offset: 0px 4px;
  shadow-opacity: 0.2;
  shadow-radius: 8px;
  elevation: 8;
`;

const FabText = styled(TextGeneric)`
  color: #FFFFFF;
  font-size: ${({theme}) => theme.text.s9}px;
  font-weight: 600;
  padding-horizontal: 12px;
`;

export interface DifferentialDiagnosisTableContextType {
  isDifferentialDiagnosis: boolean;
  isLatestTreatmentOptions?: boolean;
  toggleRowSelection: (rowIndex: number, rowData: any) => void;
  selectedRows: Set<number>;
  showTreatmentOptionsFAB: boolean;
  onTreatmentOptionsPress: () => void;
}

interface CheckboxCellProps {
  checked: boolean;
  onToggle: () => void;
}

export const CheckboxCell: React.FC<CheckboxCellProps> = ({
  checked,
  onToggle,
}) => {
  const theme = useTheme() as Theme;

  return (
    <CheckboxContainer checked={checked} theme={theme} onPress={onToggle}>
      {checked && <Icon name="check" size={16} color={theme.secondary} />}
    </CheckboxContainer>
  );
};

interface TreatmentOptionsFABProps {
  onPress: () => void;
  visible?: boolean;
}

export const TreatmentOptionsFAB: React.FC<TreatmentOptionsFABProps> = ({
  onPress,
  visible = true, // Default to true since parent already checks visibility
}) => {
  const theme = useTheme() as Theme;

  if (!visible || !isWeb) return null;

  return (
    <FabContainer>
      <FabButton onPress={onPress}>
        <FabText theme={theme}>Treatment Options</FabText>
      </FabButton>
    </FabContainer>
  );
};

interface UCAFFABProps {
  onPress: () => void;
  visible?: boolean;
}

export const UCAFFAB: React.FC<UCAFFABProps> = ({
  onPress,
  visible = true, // Default to true since parent already checks visibility
}) => {
  const theme = useTheme() as Theme;

  if (!visible) return null;

  return (
    <FabContainer style={{bottom: 80}}> {/* Position above other FAB if both exist */}
      <FabButton onPress={onPress}>
        <Icon name="document" size={20} color="#FFFFFF" />
        <FabText theme={theme}>Generate UCAF</FabText>
      </FabButton>
    </FabContainer>
  );
};

interface DrugsTreatmentFABProps {
  onPress: () => void;
  visible?: boolean;
}

export const DrugsTreatmentFAB: React.FC<DrugsTreatmentFABProps> = ({
  onPress,
  visible = true, // Default to true since parent already checks visibility
}) => {
  const theme = useTheme() as Theme;

  if (!visible || !isWeb) return null;

  return (
    <FabContainer>
      <FabButton onPress={onPress}>
        <Icon name="pill" size={20} color="#FFFFFF" />
        <FabText theme={theme}>Drugs Treatment</FabText>
      </FabButton>
    </FabContainer>
  );
};

interface DifferentialDiagnosisTableProviderProps {
  children: React.ReactNode;
  categoryId?: number;
  isStreamingCompleted?: boolean;
}

// List of differential diagnosis category IDs from the static data
const DIFFERENTIAL_DIAGNOSIS_CATEGORY_IDS = [1, 69, 53, 316];

export const DifferentialDiagnosisTableProvider: React.FC<
  DifferentialDiagnosisTableProviderProps
> = ({children, categoryId, isStreamingCompleted = false}) => {
  const {
    selectedRows,
    toggleRowSelection,
    clearSelection,
    getSelectedRowsReadable,
  } = useDifferentialDiagnosisTable();

  const isDifferentialDiagnosis = categoryId
    ? DIFFERENTIAL_DIAGNOSIS_CATEGORY_IDS.includes(categoryId)
    : false;
  const showTreatmentOptionsFAB =
    isDifferentialDiagnosis && isStreamingCompleted && selectedRows.size > 0;

  const handleTreatmentOptionsPress = useCallback(() => {
    const selectedData = getSelectedRowsReadable();
    selectedData.forEach((rowData, index) => {
    });

    // Clear selection after logging
    clearSelection();
  }, [getSelectedRowsReadable, clearSelection]);

  return (
    <>
      {React.cloneElement(children as React.ReactElement, {
        isDifferentialDiagnosis,
        toggleRowSelection,
        selectedRows,
      })}
      <TreatmentOptionsFAB
        visible={showTreatmentOptionsFAB}
        onPress={handleTreatmentOptionsPress}
      />
    </>
  );
};
